/**
 * Pure logic for the coding-session event tape (FRONT-333).
 *
 * The backend appends every session event to an append-only Postgres tape
 * (FRONT-332); the stream endpoint replays it and live-tails. This module
 * turns that raw tape into something renderable: ordered, de-duplicated
 * events → transcript items (bubbles, activity lines, run markers) and a
 * single user-facing status. Kept free of React so it's unit-testable.
 */

import { formatComputeBlocks } from '~/features/limits/compute-blocks';

export type CodingEventName =
  | 'user_message'
  | 'assistant_text'
  | 'tool_use'
  | 'tool_result'
  | 'run_started'
  // FRONT-987: the boot sweep auto-resumed a run a restart had orphaned.
  | 'run_resumed'
  | 'run_finished'
  | 'run_failed'
  | 'run_interrupted'
  | 'upload_removed'
  | 'context_cleared'
  | 'question_asked'
  | 'question_answered'
  // FRONT-3111: a consent card's "Yes" was applied. Nothing to show.
  | 'consent_used';

export interface CodingEvent {
  seq: number;
  name: CodingEventName;
  payload: Record<string, unknown>;
}

export interface CodingSessionView {
  id: string;
  projectSlug: string;
  repoPath: string;
  title: string;
  status: 'idle' | 'running' | 'failed';
  /** FRONT-1681: is this session parked on a pending ask_user question? A
   *  session in that state is ALSO `status === 'running'` — this is the
   *  durable signal the chat overview's Building Sessions rows need to tell
   *  "waiting on your answer" apart from "still building" (the in-memory
   *  run-waiting set that would otherwise say so is per-replica and resets
   *  on restart, so the backend derives this from a real pending question
   *  row instead). */
  awaitingAnswer: boolean;
  /** FRONT-1681: is a run genuinely alive for this session RIGHT NOW —
   *  computed server-side (`computeRunLive`, src/coding/routes.ts) from
   *  either this replica's own in-memory claim or a still-future run
   *  lease. `status === 'running'` alone is NOT this signal: the
   *  in-memory claim is per-replica and can only ever be ADDED, never
   *  retired for a crashed process, so a row can sit at status=running
   *  with a long-expired lease forever. The chat overview's Building
   *  Sessions rows (`mapCodingToBar`) must check this before rendering a
   *  "running" row as actually live. */
  runLive: boolean;
  /** FRONT-1681 (build lifecycle round): has the caller opened this build
   *  since its last significant transition — computed server-side
   *  (`getCodingSessionsSeen`, `src/coding/seen.ts`) off the SAME per-user
   *  `chat_seen` marker the chat spine uses, namespaced `coding:<id>`.
   *  Drives the chat overview's green→gray step for Building Sessions,
   *  the same way `done-unseen`/`done-seen` do for a chat
   *  (`state-mapping.ts`'s `classifyBuild`). */
  seen: boolean;
  lastError: string | null;
  sdkSessionId: boolean;
  autoClearEnabled: boolean;
  /** FRONT-2884: % of the model window at which auto-handover fires (server
   *  resolves the default, so this is always a number). */
  autoClearThresholdPercent: number;
  /** FRONT-3301: where `autoClearThresholdPercent` comes from — the session's
   *  own pick, the organisation default an admin set, or EDDY's default. */
  autoClearThresholdSource: 'session' | 'org' | 'default';
  /** FRONT-3301: the level this session uses without a pick of its own; the
   *  hand-over menu marks it "default". */
  autoClearDefaultPercent: number;
  /** FRONT-2219: per-session built-in capability toggles. */
  webToolsEnabled: boolean;
  knowledgeToolsEnabled: boolean;
  agentToolsEnabled: boolean;
  guidedBuildStopped: boolean;
  createdAt: string;
  updatedAt: string;
  /** FRONT-1681 (review round): when this session's last run reached a
   *  terminal state (idle after success/interrupt, or failed) — `null`
   *  means it has never run. `state-mapping.ts`'s `classifyBuild` uses this
   *  as "has this build ever run" instead of `sdkSessionId`, which is a
   *  mere resume HANDLE that auto-clear / "Clear & continue" null out on a
   *  session that definitely ran. */
  lastRunEndedAt: string | null;
  /** FRONT-1681 (third review round): how the run that stamped
   *  `lastRunEndedAt` actually ended. `null` for a never-run session, or a
   *  pre-existing one the backfill couldn't derive an outcome for —
   *  `state-mapping.ts`'s `classifyBuild` degrades a null outcome to the
   *  pre-existing "finished" wording rather than treating it as
   *  "interrupted". Only meaningful alongside a non-null `lastRunEndedAt`. */
  lastRunOutcome: 'completed' | 'failed' | 'interrupted' | null;
  /** FRONT-3175: EDDY's exec summary of the session, refreshed per reopen
   *  through POST …/summary. Null until the first run has been summarized. */
  summary?: string | null;
  summaryAt?: string | null;
  /** FRONT-2721: when the newest step on the tape was written. Only the detail
   *  GET carries it; a LIST row leaves it `undefined`. */
  lastActivityAt?: string | null;
  /** FRONT-1816: the published app this session produced. `null` when saved
   *  but the lookup returned nothing, and `undefined` on a LIST row (only the
   *  single-session GET populates it) — optional so a list consumer can't
   *  read a value the projection never set (the "shared response type is a
   *  lie" trap). Refreshed by the single-session GET, which the hook
   *  re-fetches on every run-end event, so the badge updates when Eddy
   *  republishes and posts the new link. */
  app?: CodingAppView | null;
  /** FRONT-2938: why a draft no longer has its app (its person was removed as an editor), in the words to show them. */
  appUnavailable?: string | null;
}

/**
 * FRONT-1816: the coding session's published-app facts, from the
 * single-session GET (`appViewForSession`, src/coding/routes.ts).
 */
export interface CodingAppView {
  slug: string;
  status: string;
  visibility: string;
  /** Whether a same-org colleague who is NOT the owner can open it —
   *  computed server-side through the SAME `sharedToWorkspace` clause the
   *  serve gate uses (FRONT-856), so this badge can't drift from it. This is
   *  what separates "Shared" from "Only you"; the frontend renders it
   *  rather than re-deriving the access rule. */
  colleaguesCanOpen: boolean;
  /** FRONT-1880: why the Share popover's audience choices are locked for
   *  this viewer, in the words to show them — `null` when they can choose.
   *  Computed server-side by `audienceLockReason` (`src/apps/audience.ts`),
   *  which is also what the audience route refuses with, so the control and
   *  the server can never give two different answers. Optional so an older
   *  cached payload reads as "no lock" rather than crashing the header. */
  audienceLock?: string | null;
  /** FRONT-1880: which shape "Shared" has — `people` for a named few.
   *  FRONT-2938: null, like the lists below are empty, in an editor's own draft. */
  shareScope?: 'workspace' | 'people' | null;
  /** FRONT-1880: the colleagues named on it. The control EDITS this list, so
   *  a mount without it overwrites what it was never shown. FRONT-1926: `[]`
   *  while the named share is paused (audience is `only-you`) — the ACTIVE
   *  list, never the remembered one. */
  people?: string[];
  /** FRONT-1926: the colleagues remembered from a share "Only you" paused
   *  rather than deleted. Lets the Share control offer "Share again with the
   *  same N colleagues?" instead of silently restoring them. */
  pausedPeople?: string[];
  /** FRONT-2566: do colleagues find this Public app in their lists? Null in an editor's own draft. */
  listedToColleagues?: boolean | null;
  /** Absolute when the backend knew the request origin, a relative
   *  `/apps/<slug>/` path otherwise. */
  url: string;
  /** FRONT-2940: this is an editor's own draft, so its Save App asks the owner to accept instead. */
  viewerIsEditor?: boolean;
  /** FRONT-2940: who to ask, in an editor's draft; null in the owner's own. */
  ownerName?: string | null;
  /** FRONT-3159: the viewer's own draft in its sealed preview, while both flags are on; null otherwise. */
  draftPreview?: string | null;
}

/** The three audiences the coding header badges, named after WHO can open
 *  the app — never after its lifecycle status or the action that set it.
 *
 *  FRONT-1879: the frontend TWIN of `src/apps/audience.ts`, which is the
 *  source of truth. The frontend cannot import from `src/`, so this is a
 *  hand copy — guarded by `audience-copy.drift.test.ts`, which reads the
 *  backend file and fails if the two disagree. */
export type AppAudience = 'only-you' | 'shared' | 'public';

/**
 * FRONT-1816: who can open this app, from the owner's chair (the coding
 * window is always the owner's). The one contested judgement — whether a
 * colleague can open it — is decided server-side (`colleaguesCanOpen`, via
 * `sharedToWorkspace`), so this function only routes on external
 * visibility and never re-derives the FRONT-856 access rule:
 *
 *  - public    → public    (anyone with the link opens it)
 *  - private   → shared    if colleagues can open it, else only you
 *
 * `visibility` is `private | public` (FRONT-1775 collapsed the third
 * historical value, `unlisted`, in a data migration — see
 * `src/db/schema/apps.ts`'s column comment). A stray value this function
 * has never seen falls to the private branch below, understating openness
 * rather than overstating it.
 *
 * **`app.status` is NOT read (FRONT-2091).** It used to be, as a first line
 * returning `only-you` for anything not live, which badged a colleague's
 * offline but workspace-shared — even public — app "Only you". Audience is
 * the sharing DECISION; whether it is in force right now is lifecycle, and
 * since FRONT-2078 that has its own Offline badge to carry it.
 *
 * **So this is not a reachability predicate.** `shared` on a draft means a
 * colleague WILL be able to open it once live, not that they can now
 * (`resolveServableApp` serves a draft to its owner alone). Every surface
 * rendering a present-tense sentence beside this word states the lifecycle
 * part itself — the coding header's Offline chip, the Apps row's Offline
 * badge, `whoOpensTheLink`, `describeDormantAudience`.
 *
 * The twin of this rule is `appAudienceFrom` in `src/apps/audience.ts`,
 * which is the source of truth — this is a hand copy (the frontend cannot
 * import from `src/`). Change one without the other and the two start
 * describing the same app with two different words.
 */
export function appAudience(app: CodingAppView): AppAudience {
  // FRONT-2091: no `app.status` branch here. See the docblock.
  if (app.visibility === 'public') return 'public';
  // visibility === 'private' (or any unrecognised value) from here.
  return app.colleaguesCanOpen ? 'shared' : 'only-you';
}

/** The human label + one-line meaning for each audience, shared by the
 *  badge and its tooltip so the two can never drift. */
export const APP_AUDIENCE_COPY: Record<AppAudience, { label: string; hint: string }> = {
  'only-you': { label: 'Only you', hint: 'Only you can open this app.' },
  shared: { label: 'Shared', hint: 'People in your workspace can open it and find it in Apps.' },
  public: { label: 'Public', hint: 'Anyone with the link can open it.' },
};

/**
 * Merge incoming events into the existing tape, de-duplicating by seq and
 * keeping ascending order. Reconnects replay overlapping ranges (the stream
 * URL's cursor can be stale), so the same seq arriving twice is normal.
 */
export function appendEvents(existing: CodingEvent[], incoming: CodingEvent[]): CodingEvent[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((e) => e.seq));
  const fresh = incoming.filter((e) => Number.isFinite(e.seq) && e.seq > 0 && !seen.has(e.seq));
  if (fresh.length === 0) return existing;
  return [...existing, ...fresh].sort((a, b) => a.seq - b.seq);
}

export function maxSeq(events: CodingEvent[]): number {
  return events.length === 0 ? 0 : events[events.length - 1].seq;
}

/* ------------------------------ transcript ------------------------------ */

/** One chat upload staged into the session repo (FRONT-358). Carried on the
 *  user_message tape event, so badges survive reconnect/reopen. */
export interface UploadAttachment {
  fileId: string;
  /** Sanitized repo filename — the upload's identity for delete. */
  name: string;
  /** Repo-relative path, e.g. "uploads/report.pdf". */
  path: string;
  contentType: string;
}

/** Mirrors the backend's chat-questions shapes (FRONT-707). */
export interface CodingQuestionOption {
  label: string;
  description?: string;
}

export interface CodingQuestionDef {
  question: string;
  header: string;
  multiSelect: boolean;
  options: CodingQuestionOption[];
}

export interface CodingQuestionAnswer {
  selected: string[];
  other?: string;
  /** FRONT-3155: the person skipped this question. */
  skipped?: boolean;
  /** FRONT-3155: a partial answer left this question open; it moved to a
   *  newer batch (its `remainderOf` points back here). */
  open?: boolean;
}

/** FRONT-2661: 'dismissed' is the card's own Dismiss — closed without
 *  answers like 'stopped', but only that card; guided build stays on. */
export type QuestionOutcome = 'answered' | 'self_answered' | 'stopped' | 'dismissed';

export type TranscriptItem =
  | { kind: 'user'; seq: number; text: string; attachments: UploadAttachment[] }
  | { kind: 'assistant'; seq: number; text: string }
  | {
      kind: 'activity';
      seq: number;
      tool: string;
      label: string;
      /** Pretty-printed tool input, for the expandable detail view. */
      input: string;
      result: 'pending' | 'ok' | 'error';
      resultContent: string;
    }
  | {
      kind: 'marker';
      seq: number;
      variant: 'started' | 'finished' | 'failed' | 'interrupted' | 'paused' | 'cleared';
      text: string;
    }
  | {
      /** An ask_user question card (FRONT-707). Pending until a
       *  question_answered event lands for the same batch. */
      kind: 'questions';
      seq: number;
      batchId: number;
      mode: 'interview' | 'build';
      /** FRONT-917: set only on the single go/adjust question that gates
       *  starting the build. FRONT-1160: also the signal that hides Skip on
       *  the card and keeps the Builder's interview bar (and its stop exit,
       *  FRONT-2478) away — both exist to shortcut the INTERVIEW, and a
       *  plain yes/no gate is not one. */
      confirmsBuild?: boolean;
      /** FRONT-1307: the answer must resolve to one of the offered options —
       *  the asking tool cannot act on typed words, so the card drops its
       *  free-text "Other" row. Set by the publish gate only; the go/adjust
       *  gate keeps Other, since an adjustment written in the box is an
       *  answer it handles. */
      requiresOption?: boolean;
      /** FRONT-2340: a yes/no consent card for an action — never counts as
       *  an interview round (no interview bar), without being the build gate. */
      consentCard?: boolean;
      questions: CodingQuestionDef[];
      answers?: CodingQuestionAnswer[];
      outcome?: QuestionOutcome;
      /** FRONT-3155: the person's typed note that went along with the answers. */
      note?: string;
      /** FRONT-3155: this batch holds the questions a PARTIAL answer to batch
       *  `remainderOf` left open. No run is blocked on it (the tool returned),
       *  so it never counts as "the agent is waiting" (findOpenBatchOrBoundary). */
      remainderOf?: number;
    };

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Defensive parse of the attachments array on a user_message payload —
 *  history rows from older builds simply have none. */
export function readUploadAttachments(payload: Record<string, unknown>): UploadAttachment[] {
  const raw = payload.attachments;
  if (!Array.isArray(raw)) return [];
  const out: UploadAttachment[] = [];
  for (const entry of raw) {
    const a = entry as Partial<UploadAttachment> | null;
    if (!a || typeof a.name !== 'string' || !a.name) continue;
    out.push({
      fileId: typeof a.fileId === 'string' ? a.fileId : '',
      name: a.name,
      path: typeof a.path === 'string' && a.path ? a.path : `uploads/${a.name}`,
      contentType: typeof a.contentType === 'string' ? a.contentType : '',
    });
  }
  return out;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** "mcp__repo__write_file" → "write_file" (display only). */
export function shortToolName(tool: string): string {
  const i = tool.lastIndexOf('__');
  return i >= 0 ? tool.slice(i + 2) : tool;
}

/**
 * A tool call's `input` as an object, for the two label functions below.
 *
 * The tape stores it as a JSON string written by the model, so it can be
 * malformed. That is not worth throwing over: these functions render a label
 * inside a live transcript, and an exception here would take the row — or the
 * strip — down over a cosmetic string. The recovery IS the return value: an
 * empty object, which every caller already handles by falling back to a
 * generic label ("Wrote a file" rather than a path).
 *
 * Deliberately not logged. This runs per tool call on every render pass, so a
 * warning here would repeat for the life of the session on a single bad
 * payload, and the degraded label is already visible on screen.
 */
function parseToolInput(inputJson: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(inputJson || '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * One human-readable line per tool call — "Wrote src/app.js", "Ran: npm
 * test". Input arrives as a JSON string on the tape; unparseable input falls
 * back to a generic label rather than throwing mid-render.
 */
export function summarizeToolUse(tool: string, inputJson: string): string {
  const name = shortToolName(tool);
  const input = parseToolInput(inputJson);
  const path = str(input.path);
  switch (name) {
    case 'write_file':
      return input.append ? `Appended to ${path || 'a file'}` : `Wrote ${path || 'a file'}`;
    case 'read_file':
      return `Read ${path || 'a file'}`;
    case 'view_image':
      return `Looked at ${path || 'an image'}`;
    case 'list_files':
      return path ? `Listed ${path}` : 'Listed project files';
    case 'search_files':
      return `Searched for ${truncate(str(input.query), 60) || 'files'}`;
    case 'run_command':
      return `Ran: ${truncate(str(input.cmd), 80) || 'a command'}`;
    case 'stop_guided_questions':
      return 'Turned off guided questions for this session';
    case 'name_project': {
      const projectName = truncate(str(input.name), 60);
      return projectName ? `Named the project “${projectName}”` : 'Named the project';
    }
    // FRONT-2340: the app-config tools the session uses on its own app.
    case 'app_settings_update':
      return "Updated the app's settings";
    case 'search_wireable_tools':
      return `Searched agent tools for ${truncate(str(input.query), 60) || 'a capability'}`;
    case 'list_custom_agents':
      return 'Checked your agents';
    case 'get_custom_agent':
      return 'Read an agent';
    case 'create_custom_agent':
      return `Created agent ${truncate(str(input.name), 60) || ''}`.trim();
    case 'update_custom_agent':
      return 'Updated an agent';
    case 'app_options':
      return 'Checked what the app can use';
    // FRONT-2399: the builder's browser.
    case 'open_preview':
      return `Opened the draft${str(input.size) === 'phone' ? ' at phone size' : ''}`;
    case 'open_page':
      return `Looked at ${truncate(str(input.url), 60) || 'a web page'}`;
    case 'screenshot':
      return 'Took a screenshot';
    case 'click':
      return 'Clicked on the page';
    case 'fill':
      return 'Typed into the page';
    case 'read_page':
      return 'Read the page';
    case 'page_errors':
      return 'Checked the page for errors';
    case 'close_page':
      return 'Closed a page';
    default:
      return `Used ${name}`;
  }
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/**
 * Tape → ordered transcript items. Tool results attach to their tool_use by
 * toolUseId (an activity stays "pending" until its result lands). Run
 * boundary events become slim markers; the run_finished result text is NOT
 * repeated as a bubble — the assistant_text events already streamed it.
 */
/** The blocking question tools (FRONT-707, FRONT-1325) — their raw
 *  tool_use/tool_result activity rows are suppressed; the question card is
 *  the representation. confirm_build blocks the exact same way ask_user
 *  does (it's the same askAndPoll underneath), so it needs the identical
 *  exclusion or its card would sit behind a spinning activity row for up to
 *  the full ask timeout. */
export const BLOCKING_QUESTION_TOOLS = new Set(['mcp__ask__ask_user', 'mcp__ask__confirm_build']);

function readQuestionDefs(raw: unknown): CodingQuestionDef[] {
  if (!Array.isArray(raw)) return [];
  const out: CodingQuestionDef[] = [];
  for (const entry of raw) {
    const q = entry as Partial<CodingQuestionDef> | null;
    if (!q || typeof q.question !== 'string' || !Array.isArray(q.options)) continue;
    out.push({
      question: q.question,
      header: typeof q.header === 'string' ? q.header : '',
      multiSelect: q.multiSelect !== false,
      options: q.options
        .filter((o): o is CodingQuestionOption => !!o && typeof o.label === 'string')
        .map((o) => ({ label: o.label, ...(o.description ? { description: o.description } : {}) })),
    });
  }
  return out;
}

function readQuestionAnswers(raw: unknown): CodingQuestionAnswer[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const a = entry as Partial<CodingQuestionAnswer> | null;
    return {
      selected: Array.isArray(a?.selected)
        ? a.selected.filter((s): s is string => typeof s === 'string')
        : [],
      ...(typeof a?.other === 'string' && a.other ? { other: a.other } : {}),
      ...(a?.skipped === true ? { skipped: true } : {}),
      ...(a?.open === true ? { open: true } : {}),
    };
  });
}

/** Shown on a step whose run ended before its result landed (FRONT-2471). */
export const UNFINISHED_ACTIVITY_TEXT = "Didn't finish: the run ended before this step completed.";

/**
 * FRONT-2471: `run_started` proves every earlier run is over (it is written
 * only after the exclusive claim; `run_resumed` is not, so it proves
 * nothing), so a tool row still waiting for its result belongs to a run that
 * died (a backend restart kills the process mid-tool and no tool_result is
 * ever written).
 * Left alone it spins forever above the new run.
 */
function settleUnfinishedActivities(
  byToolUseId: Map<string, Extract<TranscriptItem, { kind: 'activity' }>>,
): void {
  for (const item of byToolUseId.values()) {
    if (item.result !== 'pending') continue;
    item.result = 'error';
    item.resultContent = UNFINISHED_ACTIVITY_TEXT;
  }
  byToolUseId.clear();
}

export function buildTranscript(events: CodingEvent[]): TranscriptItem[] {
  const items: TranscriptItem[] = [];
  const activityByToolUseId = new Map<string, Extract<TranscriptItem, { kind: 'activity' }>>();
  const questionsByBatchId = new Map<number, Extract<TranscriptItem, { kind: 'questions' }>>();
  // FRONT-3084: whether the newest run is the background check, for how its end reads.
  let inBackgroundCheck = false;

  for (const event of events) {
    const p = event.payload ?? {};
    switch (event.name) {
      case 'user_message':
        items.push({
          kind: 'user',
          seq: event.seq,
          text: str(p.text),
          attachments: readUploadAttachments(p),
        });
        break;
      case 'assistant_text': {
        const text = str(p.text);
        if (text.trim()) items.push({ kind: 'assistant', seq: event.seq, text });
        break;
      }
      case 'tool_use': {
        const tool = str(p.tool);
        // The ask_user call is rendered as its question card, not as an
        // activity row (which would spin for minutes while it blocks).
        if (BLOCKING_QUESTION_TOOLS.has(tool)) break;
        const item: Extract<TranscriptItem, { kind: 'activity' }> = {
          kind: 'activity',
          seq: event.seq,
          tool,
          label: summarizeToolUse(tool, str(p.input)),
          input: str(p.input),
          result: 'pending',
          resultContent: '',
        };
        items.push(item);
        const toolUseId = str(p.toolUseId);
        if (toolUseId) activityByToolUseId.set(toolUseId, item);
        break;
      }
      case 'tool_result': {
        const match = activityByToolUseId.get(str(p.toolUseId));
        if (match) {
          match.result = p.isError ? 'error' : 'ok';
          match.resultContent = str(p.content);
        }
        break;
      }
      case 'run_started':
        settleUnfinishedActivities(activityByToolUseId);
        inBackgroundCheck = isBackgroundCheckStart(event);
        items.push({
          kind: 'marker',
          seq: event.seq,
          variant: 'started',
          text: inBackgroundCheck
            ? 'Your link is live. EDDY is checking the rest of the app in the background'
            : 'EDDY started working',
        });
        break;
      // FRONT-987: reuses the 'started' marker (a resume IS a run starting) and
      // carries the reason in the text, so no new variant/styling is needed.
      case 'run_resumed':
        items.push({
          kind: 'marker',
          seq: event.seq,
          variant: 'started',
          text: 'Reconnected after a backend restart — picking up where we left off',
        });
        break;
      case 'run_finished': {
        if (isErroredFinish(event)) {
          items.push({
            kind: 'marker',
            seq: event.seq,
            variant: 'interrupted',
            text: inBackgroundCheck
              ? 'Background check did not finish. Your link still works'
              : 'Stopped early: an error cut the reply short. Send "Continue" to pick it up',
          });
          break;
        }
        const turns = typeof p.numTurns === 'number' ? p.numTurns : 0;
        const duration = formatDuration(typeof p.durationMs === 'number' ? p.durationMs : NaN);
        const parts = [turns > 0 ? `${turns} steps` : '', duration].filter(Boolean).join(' in ');
        const done = inBackgroundCheck ? 'Background check done' : 'Done';
        items.push({
          kind: 'marker',
          seq: event.seq,
          variant: 'finished',
          text: parts ? `${done} — ${parts}` : done,
        });
        break;
      }
      case 'run_failed': {
        if (inBackgroundCheck) {
          items.push({
            kind: 'marker',
            seq: event.seq,
            variant: 'interrupted',
            text: 'Background check did not finish. Your link still works',
          });
          break;
        }
        if (isStepLimitPause(event)) {
          const turns = typeof p.numTurns === 'number' ? p.numTurns : 0;
          items.push({
            kind: 'marker',
            seq: event.seq,
            variant: 'paused',
            text: turns > 0 ? `Paused after ${turns} steps` : 'Paused at the step limit',
          });
          break;
        }
        items.push({
          kind: 'marker',
          seq: event.seq,
          variant: 'failed',
          text: `Run failed${str(p.reason) ? ` — ${str(p.reason)}` : ''}`,
        });
        break;
      }
      case 'run_interrupted':
        items.push({
          kind: 'marker',
          seq: event.seq,
          variant: 'interrupted',
          text: inBackgroundCheck ? 'Background check stopped for your message' : 'Stopped by you',
        });
        break;
      case 'context_cleared':
        items.push({ kind: 'marker', seq: event.seq, variant: 'cleared', text: 'Context cleared — continuing from the handover' });
        break;
      case 'question_asked': {
        const batchId = typeof p.batchId === 'number' ? p.batchId : NaN;
        if (!Number.isFinite(batchId)) break;
        const item: Extract<TranscriptItem, { kind: 'questions' }> = {
          kind: 'questions',
          seq: event.seq,
          batchId,
          mode: p.mode === 'build' ? 'build' : 'interview',
          confirmsBuild: p.confirmsBuild === true,
          requiresOption: p.requiresOption === true,
          consentCard: p.consentCard === true,
          questions: readQuestionDefs(p.questions),
          ...(typeof p.remainderOf === 'number' ? { remainderOf: p.remainderOf } : {}),
        };
        items.push(item);
        questionsByBatchId.set(batchId, item);
        break;
      }
      case 'question_answered': {
        const batchId = typeof p.batchId === 'number' ? p.batchId : NaN;
        const match = Number.isFinite(batchId) ? questionsByBatchId.get(batchId) : undefined;
        if (match) {
          // FRONT-1667: a "stopped" outcome closes the card WITHOUT answers —
          // setting `answers` here (even to []) would make the card render as
          // answered, since the card's `answered` check is `!!answers`.
          if (p.outcome === 'stopped' || p.outcome === 'dismissed') {
            match.outcome = p.outcome;
          } else {
            match.answers = readQuestionAnswers(p.answers);
            if (typeof p.note === 'string' && p.note.trim()) match.note = p.note;
            match.outcome = p.outcome === 'self_answered' ? 'self_answered' : 'answered';
          }
        }
        break;
      }
    }
  }
  return items;
}

/**
 * The tape's newest question batch, if it has no ANSWER (or cancel) yet —
 * FRONT-1308. Despite the shared name, this is NOT the same rule as
 * `hasUnansweredQuestion` below: this scans transcript ITEMS and never looks
 * at run-boundary markers, so a card whose run has since wound down (a
 * timeout, or an interrupt) still reads as open here — it IS still
 * clickable, since answering it later starts a continuation run. That gap is
 * exactly what makes this function the wrong predicate for whether the
 * COMPOSER should route free text into the batch (FRONT-1708) — use
 * `findBlockingQuestionItem` below for that. The last 'questions' item found
 * scanning backward is always the newest batch, by construction
 * (buildTranscript appends in tape order).
 */
export function findOpenQuestionItem(
  transcript: TranscriptItem[],
): Extract<TranscriptItem, { kind: 'questions' }> | null {
  for (let i = transcript.length - 1; i >= 0; i--) {
    const item = transcript[i];
    if (item.kind === 'questions') {
      // FRONT-1667 (live-discovered): a "stopped" outcome closes the card
      // WITHOUT ever setting `answers` — that's the whole point, no
      // fabricated answer — so `answers` truthiness alone can't tell open
      // from closed any more. Without this, the composer kept routing every
      // message after a "Stop asking" click into answerQuestions against
      // the now-cancelled batch (409, message silently dropped) instead of
      // sending it normally.
      // FRONT-2661: a dismissed card is closed the same way.
      const closed = Boolean(item.answers) || item.outcome === 'stopped' || item.outcome === 'dismissed';
      return closed ? null : item;
    }
  }
  return null;
}

/**
 * The open batch a LIVE run is actually BLOCKED on, if any (FRONT-1708) —
 * the narrow predicate the composer's send behaviour (and its placeholder)
 * must use, never `findOpenQuestionItem` above. Reuses `hasUnansweredQuestion`'s
 * own boundary rule rather than re-deriving it, so this can never drift from
 * what `deriveStatus`'s 'waiting' status already means: once a run boundary
 * (finished/failed/interrupted) lands after the ask, nothing is waiting on
 * anyone any more, even though the card itself still has no answer. Before
 * this existed, a timed-out or interrupted card stayed "open" by
 * `findOpenQuestionItem`'s reckoning, and the composer silently wrote
 * whatever the user typed next as the ANSWER to a batch nothing was blocking
 * on (the ticket's own repro: a reply typed after the run had already
 * finished landed as the answer to two unrelated questions). A free-text
 * send here should start a NEW run instead.
 *
 * `transcript` and `events` MUST describe the SAME tape (i.e. `transcript`
 * came from `buildTranscript(events)`) — nothing enforces that at the type
 * level, so a caller passing a transcript built from a different or stale
 * events array gets a silently wrong answer.
 *
 * `sessionStatus` is optional so existing callers keep their behaviour
 * unchanged; pass it to also catch FRONT-1850: the boot-time orphan sweep
 * marks the session row 'failed' without ever appending a run-boundary
 * event to the tape (unlike every other failure path), so the scan below
 * still sees an open batch forever. Reuses `deriveStatus`'s own 'failed'
 * guard on the identical open_batch case, so the two can never drift on
 * what a dead run means again.
 */
export function findBlockingQuestionItem(
  transcript: TranscriptItem[],
  events: CodingEvent[],
  sessionStatus?: CodingSessionView['status'],
): Extract<TranscriptItem, { kind: 'questions' }> | null {
  if (sessionStatus === 'failed') return null;
  return hasUnansweredQuestion(events) ? findOpenQuestionItem(transcript) : null;
}

/**
 * FRONT-3155: the open batch the Builder's answer panel (at the reply box)
 * offers — ANY open card, not only one the run is blocked on, because
 * answering a timed-out or detached card later starts a continuation. Not once
 * the run died (FRONT-2106: a card on a dead run cannot be answered), and not a
 * batch this window already sent an answer for (`sentBatchIds`, whose tape
 * event is still in flight). Deliberately NOT the composer's send-routing
 * predicate: whether typed text may answer it is `findBlockingQuestionItem`'s
 * call alone (FRONT-1708).
 */
export function findAnswerPanelItem(
  transcript: TranscriptItem[],
  runStatus: CodingStatus | undefined,
  sentBatchIds: readonly number[],
): Extract<TranscriptItem, { kind: 'questions' }> | null {
  if (runStatus === 'failed') return null;
  const open = findOpenQuestionItem(transcript);
  return open && !sentBatchIds.includes(open.batchId) ? open : null;
}

/* -------------------------------- status -------------------------------- */

/** FRONT-3084: `checking` is the background check after a publish: the link
 *  already works, and a new message stops the check instead of waiting. */
export type CodingStatus = 'working' | 'checking' | 'waiting' | 'done' | 'failed';

/** FRONT-3084: the run this event starts is the background check after a publish. */
export function isBackgroundCheckStart(event: CodingEvent): boolean {
  return event.name === 'run_started' && event.payload?.background === true;
}

/** FRONT-3084: whether the tape's newest run is the background check. */
function newestRunIsBackgroundCheck(events: CodingEvent[]): boolean {
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].name === 'run_started') return isBackgroundCheckStart(events[i]);
  }
  return false;
}

function eventBatchId(event: CodingEvent): number {
  const raw = event.payload?.batchId;
  return typeof raw === 'number' ? raw : NaN;
}

/**
 * Scans the tape backward for whichever comes first: a run-boundary event,
 * or a question_asked batch with no matching question_answered yet. The
 * tape is append-only and an answer always lands after its own ask, so
 * scanning backward hits a batch's answer (if any) before its ask — the
 * first unanswered ask we hit is therefore the newest OPEN batch, and any
 * older batch is either already answered or irrelevant (its run already
 * ended, per the boundary short-circuit below). Shared by deriveStatus (the
 * 'waiting' status) and computeQueueBusy's fallback (FRONT-1308) so the two
 * can't drift on what "pending" means — mirrors coding-questions-card.tsx's
 * own `pending = !answered && !superseded && interactive` at the tape level.
 */
/** A run that reached its step limit: it paused, and the next message resumes it (FRONT-2972). */
export function isStepLimitPause(event: CodingEvent): boolean {
  return event.name === 'run_failed' && event.payload?.reason === 'error_max_turns';
}

/** A run the SDK reported as finished although an API error cut it short, e.g. the output token limit (FRONT-3248). */
export function isErroredFinish(event: CodingEvent): boolean {
  return event.name === 'run_finished' && event.payload?.isError === true;
}

function findOpenBatchOrBoundary(
  events: CodingEvent[],
):
  | { kind: 'boundary'; name: 'run_started' | 'run_finished' | 'run_failed' | 'run_interrupted' | 'run_paused'; background?: boolean }
  | { kind: 'open_batch' }
  | { kind: 'none' } {
  const answeredBatchIds = new Set<number>();
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    switch (event.name) {
      case 'run_failed':
        return { kind: 'boundary', name: isStepLimitPause(event) ? 'run_paused' : 'run_failed' };
      case 'run_started':
        return { kind: 'boundary', name: event.name, background: isBackgroundCheckStart(event) };
      case 'run_finished':
        return { kind: 'boundary', name: isErroredFinish(event) ? 'run_paused' : 'run_finished' };
      case 'run_interrupted':
        return { kind: 'boundary', name: event.name };
      case 'question_answered': {
        const batchId = eventBatchId(event);
        if (Number.isFinite(batchId)) answeredBatchIds.add(batchId);
        break;
      }
      case 'question_asked': {
        const batchId = eventBatchId(event);
        // FRONT-3155: a batch split off by a partial answer has no run blocked
        // on it — the tool already returned — so it is not "the agent is
        // waiting on you" and must not flip the session to 'waiting'.
        if (typeof event.payload?.remainderOf === 'number') break;
        if (Number.isFinite(batchId) && !answeredBatchIds.has(batchId)) return { kind: 'open_batch' };
        break;
      }
    }
  }
  return { kind: 'none' };
}

/** Whether the tape's newest question batch has no answer yet (FRONT-1308). */
export function hasUnansweredQuestion(events: CodingEvent[]): boolean {
  return findOpenBatchOrBoundary(events).kind === 'open_batch';
}

/**
 * The ticket's status affordance: working / waiting for you / done (+ failed).
 * The tape is authoritative — the session row is only refetched after
 * terminal events, so between refetches it can lag behind what the stream
 * already delivered. The latest run-boundary event on the tape decides;
 * the row status is the fallback for an empty tape.
 */
export function deriveStatus(
  sessionStatus: CodingSessionView['status'] | undefined,
  events: CodingEvent[],
): CodingStatus {
  const scan = findOpenBatchOrBoundary(events);
  if (scan.kind === 'open_batch') {
    // FRONT-1308: the agent is blocked server-side on this exact answer —
    // the user is the blocker, not the run. Same orphaned-run guard as
    // below: a backend restart can strand a run mid-question just as
    // easily as mid-tool-call.
    return sessionStatus === 'failed' ? 'failed' : 'waiting';
  }
  if (scan.kind === 'boundary') {
    switch (scan.name) {
      case 'run_started':
        // A tape that ends mid-run normally means "working" — EXCEPT when
        // the session row says failed: the boot sweep marks sessions whose
        // run was orphaned by a backend restart, and no terminal event ever
        // lands on their tape. Without this the window shows a permanent
        // "working" with a locked composer and the session can never be
        // resumed (FRONT-334).
        if (sessionStatus === 'failed') return 'failed';
        return scan.background ? 'checking' : 'working';
      case 'run_finished':
        return 'done';
      // FRONT-3084: however the background check ended, the published build
      // it checked is what the session is — the link works.
      case 'run_failed':
        return newestRunIsBackgroundCheck(events) ? 'done' : 'failed';
      case 'run_interrupted':
      case 'run_paused':
        return newestRunIsBackgroundCheck(events) ? 'done' : 'waiting';
    }
  }
  if (sessionStatus === 'running') return 'working';
  if (sessionStatus === 'failed') return 'failed';
  return 'waiting';
}

/**
 * The Build window and the top bar must agree on whether EDDY is working
 * (eddy, 2026-10-26: "builder status is blue, but in the main bar it is
 * gray"). The window reads its own tape, which ends on `run_started` forever
 * when the process running it died (a backend restart) — the top bar reads
 * the server's `runLive`, which knows. When the server says the run is no
 * longer alive, the window says so too; when it is resumed (`run_resumed`, a
 * fresh lease) both turn back to working together. Only a `running` row with
 * `runLive === false` overrides: a missing or stale row (a run that just
 * started, the list not yet polled) never does, so nothing flickers.
 */
export function reconcileWithServerLiveness(
  status: CodingStatus,
  row: Pick<CodingSessionView, 'status' | 'runLive'> | undefined,
): CodingStatus {
  if ((status === 'working' || status === 'checking') && row?.status === 'running' && row.runLive === false) return 'waiting';
  return status;
}

/**
 * BUG-037 / FRONT-850: the queued-send auto-fire (chat-input.tsx's `queueBusy` prop)
 * needs to know when a run is REALLY done, not just when the local SSE tape
 * thinks so. The tape can freeze on "working" forever if the stream drops
 * or the server closes it as idle before a terminal event lands — its own
 * reconnect only retries while the frozen tape still looks busy, so nothing
 * ever wakes it back up.
 *
 * The independently-polled session row (mirroring the inbox modal's
 * `useBackgroundChatForSession`) is the second opinion that un-sticks it —
 * and it must be the TIE-BREAKER, not just an extra way to become MORE busy.
 * A naive OR of "local says working" with "polled says running" is wrong:
 * once the tape freezes on working, that OR always short-circuits true and
 * the poll can never override it back to false — reproducing the exact bug
 * this function exists to fix. Once we have a fresh poll result, trust it;
 * only fall back to the (possibly stale) local tape before the first poll
 * response ever lands.
 *
 * `pendingQuestion` (FRONT-1308) covers the same fallback window for an
 * open question batch: deriveStatus now reports 'waiting' there rather than
 * 'working', but the run is still genuinely in flight, so the fallback OR's
 * it back in — see `hasUnansweredQuestion`.
 */
export function computeQueueBusy(
  localStatus: CodingStatus,
  polledStatus: CodingSessionView['status'] | null | undefined,
  pendingQuestion = false,
): boolean {
  // FRONT-3084: the server stops the background check for a new message, so
  // nothing waits behind it — even though the row says "running".
  if (localStatus === 'checking') return false;
  if (polledStatus != null) return polledStatus === 'running';
  // FRONT-1308: before the first poll response lands, an open question
  // batch makes deriveStatus report 'waiting' rather than 'working' — but
  // the run is still in flight server-side, so the queued-send auto-fire
  // must stay held rather than firing a draft into it.
  return localStatus === 'working' || pendingQuestion;
}

/* --------------------------- context size (FRONT-645) -------------------- */

export interface ContextUsage {
  /** Tokens occupying the model context as of the last completed run — the
   *  full prompt size of that run's final model call. Read off the event's
   *  own `usage.contextTokens`, stamped server-side (FRONT-1793,
   *  `contextTokensFromSdkMessage` in src/coding/runner.ts). */
  contextTokens: number;
  /**
   * Denominator: the coding model's real context window, resolved
   * server-side (FRONT-1651, `resolveCodingContextWindow` in
   * src/coding/context-window.ts) and carried on the event's own `usage`
   * payload. `null` for a tape event written before that change, when the
   * window simply isn't known.
   */
  windowTokens: number | null;
  /**
   * contextTokens / windowTokens as a percentage — NOT clamped to 100. A run
   * that ends above the window is real information (the ceiling was wrong,
   * or the run genuinely overflowed) and clamping it hid exactly that.
   * `null` when `windowTokens` is `null`.
   */
  percent: number | null;
}

/**
 * Context-size meter input (FRONT-645). The Claude Agent SDK owns the coding
 * conversation's context; Eddy only learns its size after the fact, from the
 * usage the SDK reports on each finished run (backend runner). So this reads
 * the MOST RECENT run_finished/run_failed event carrying usage and takes the
 * occupancy the runner stamped on it (`contextTokens` — see FRONT-1793 below;
 * summing input + cache-read is the LEGACY FALLBACK only, never the intended
 * path). It is therefore a post-hoc estimate: it refreshes after each run, not
 * live mid-run, and can fall after the SDK compacts. Returns null until a run
 * with usage has landed.
 *
 * FRONT-1651: the window is read off the event's own `usage.contextWindowTokens`
 * (stamped by the backend runner from `resolveCodingContextWindow()`) rather
 * than a hardcoded constant, so it tracks whatever model actually ran. An
 * older tape event never carries that field — `windowTokens`/`percent` come
 * back `null` in that case, but `contextTokens` is still returned so the
 * count can be shown.
 *
 * FRONT-1793: the NUMERATOR comes off the event too, as `usage.contextTokens`.
 * Summing `inputTokens + cacheReadTokens` — what this did before — adds up the
 * run's whole billing total across every turn, and each turn re-reads the
 * cached conversation, so a long run reported multiples of the window (320% of
 * 1,000,000 in the reported case). Those two fields still carry the run totals
 * for cost reporting and must not be repurposed; the sum survives here only as
 * the fallback for a tape event written before the stamp existed, where an
 * inflated number still beats no meter at all.
 */
/**
 * FRONT-1746: which events can carry a `usage` block. The run boundaries have
 * always carried one; the assistant frames now carry the occupancy half too,
 * which is the entire mechanism behind the meter moving DURING a run rather
 * than only at the end (`runUsageStamp` in src/coding/runner.ts).
 */
const EVENTS_CARRYING_USAGE = new Set<CodingEventName>([
  'run_finished',
  'run_failed',
  'assistant_text',
  'tool_use',
]);

/**
 * The meter's percentage: whole percent, rounded, deliberately NOT clamped —
 * see the `ContextUsage.percent` doc. FRONT-3077: Pro Builder's header meter
 * maps its own session reading through this too, so the two windows round the
 * same number the same way.
 */
export function contextPercent(contextTokens: number, windowTokens: number): number {
  return Math.round((contextTokens / windowTokens) * 100);
}

export function deriveContextUsage(events: CodingEvent[]): ContextUsage | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (!EVENTS_CARRYING_USAGE.has(e.name)) continue;
    const usage = e.payload?.usage;
    if (!usage || typeof usage !== 'object') continue;
    const u = usage as Record<string, unknown>;
    const input = typeof u.inputTokens === 'number' ? u.inputTokens : 0;
    const cacheRead = typeof u.cacheReadTokens === 'number' ? u.cacheReadTokens : 0;
    const stamped = typeof u.contextTokens === 'number' && u.contextTokens > 0 ? u.contextTokens : null;
    const contextTokens = stamped ?? input + cacheRead;
    if (contextTokens <= 0) continue;
    const windowTokens =
      typeof u.contextWindowTokens === 'number' && u.contextWindowTokens > 0
        ? u.contextWindowTokens
        : null;
    return {
      contextTokens,
      windowTokens,
      percent: windowTokens != null ? contextPercent(contextTokens, windowTokens) : null,
    };
  }
  return null;
}

/* ---------------------------- finished-run summary ----------------------- */

/** What the LAST finished run cost, for the header strip's "5 steps · 5m 43s". */
export interface RunSummary {
  /** Turns the run took. `0` when the tape event carried no turn count. */
  steps: number;
  /** Wall-clock duration. `null` when the tape event carried no duration. */
  durationMs: number | null;
}

/** Every event that opens or closes a run. The LAST of these in the tape is
 *  what decides whether there is a completed tally to quote.
 *
 *  This set deliberately DIFFERS from `findOpenBatchOrBoundary`'s: that one
 *  omits `run_resumed`, this one includes it. Do not "align" them — a resumed
 *  run is in flight and has no completed tally, and folding `run_resumed` out
 *  of here re-opens the stale-tally bug this function exists to prevent. The
 *  disagreement window is one event wide in practice (the backend appends
 *  `run_resumed` and then immediately `run_started`). */
const RUN_BOUNDARY_EVENTS = new Set<CodingEventName>([
  'run_started',
  'run_resumed',
  'run_finished',
  'run_failed',
  'run_interrupted',
]);

/**
 * The most recent run's shape, but ONLY when that run finished cleanly.
 *
 * The last run BOUNDARY decides, and anything other than `run_finished`
 * returns null: a failure, an interrupt, or a run still open all mean there
 * is no completed tally. Scanning backwards past such a boundary to an older
 * `run_finished` is precisely how a previous run's numbers end up printed
 * beside "Build failed" — or, worse, replacing the live activity label while
 * the current run is still going.
 *
 * Both fields degrade independently — an older tape event that carried only
 * one of the two still yields the half it has, rather than nothing.
 */
export function deriveRunSummary(events: CodingEvent[]): RunSummary | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (!RUN_BOUNDARY_EVENTS.has(e.name)) continue;
    if (e.name !== 'run_finished' || isErroredFinish(e)) return null;
    const p = e.payload ?? {};
    const steps = typeof p.numTurns === 'number' && p.numTurns > 0 ? p.numTurns : 0;
    const durationMs =
      typeof p.durationMs === 'number' && Number.isFinite(p.durationMs) && p.durationMs >= 0
        ? p.durationMs
        : null;
    if (steps === 0 && durationMs == null) return null;
    return { steps, durationMs };
  }
  return null;
}

/**
 * The strip's meta line for a finished run — "5 steps · 5m 43s", either half
 * on its own, or `null` when there is nothing to say.
 *
 * Singular is handled: a one-turn run reads "1 step", not "1 steps".
 */
export function formatRunSummary(summary: RunSummary | null): string | null {
  if (!summary) return null;
  const parts: string[] = [];
  if (summary.steps > 0) parts.push(`${summary.steps} ${summary.steps === 1 ? 'step' : 'steps'}`);
  if (summary.durationMs != null) parts.push(formatDuration(summary.durationMs));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/* ---------------------------- session totals ----------------------------- */

/**
 * What this whole session has cost so far — the numbers behind the header
 * menu's stats block (FRONT-2258).
 *
 * Summed across every run on the tape rather than read off the newest one:
 * the block describes the SESSION, so a project with six runs behind it must
 * not report the last run's two minutes as its whole life. `run_failed`
 * contributes its turns (that work really happened and was really billed)
 * but carries neither a duration nor a cost on the tape, so those two halves
 * simply never accumulate from it.
 *
 * Cost is not read here (FRONT-2962): it is what the platform charged for
 * each run, which the server reads back — see `useCodingSessionCost`.
 *
 * Returns null when there is nothing to report — no run on the tape at all,
 * or runs that carried none of the three numbers. A brand-new project then
 * renders no block instead of three zeros, which read as a broken statistic
 * rather than as "nothing has run yet". Same rule as `deriveRunSummary`.
 */
export interface SessionStats {
  /** Model turns across every run, finished or failed. */
  turns: number;
  /** Wall-clock milliseconds across every completed run. */
  durationMs: number;
}

export function deriveSessionStats(events: CodingEvent[]): SessionStats | null {
  let turns = 0;
  let durationMs = 0;
  for (const e of events) {
    if (e.name !== 'run_finished' && e.name !== 'run_failed') continue;
    const p = e.payload ?? {};
    // Each field is read independently and each guards itself: a tape event
    // written before one of them existed still contributes the halves it has,
    // the same way `deriveRunSummary` degrades.
    if (typeof p.numTurns === 'number' && Number.isFinite(p.numTurns) && p.numTurns > 0) {
      turns += p.numTurns;
    }
    if (typeof p.durationMs === 'number' && Number.isFinite(p.durationMs) && p.durationMs > 0) {
      durationMs += p.durationMs;
    }
  }
  if (turns === 0 && durationMs === 0) return null;
  return { turns, durationMs };
}

/**
 * FRONT-2962: how many runs on the tape have ended (finished, failed or
 * stopped) — the session cost is re-read whenever this moves.
 */
export function countEndedRuns(events: CodingEvent[]): number {
  let n = 0;
  for (const e of events) {
    if (e.name === 'run_finished' || e.name === 'run_failed' || e.name === 'run_interrupted') n += 1;
  }
  return n;
}

/**
 * Hours-aware duration for the stats block ("1h 12m").
 *
 * `formatDuration` above is deliberately left alone: it quotes a SINGLE run
 * in the lamp tooltip, where "5m 43s" is the right answer and an hours split
 * would be noise. A session total is the opposite — it routinely passes an
 * hour, and "84m" is a number the reader has to convert themselves.
 */
export function formatSessionDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0m';
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 1) return `${Math.max(1, Math.round(ms / 1000))}s`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * The stats block's cost, always in compute blocks: users are never shown
 * dollars. FRONT-2962: the platform's charge reads plain ("1.1M CB"), an
 * estimate keeps the tilde ("~1.1M CB"). Not known yet, or unpriced, reads as
 * a dash rather than "0 CB".
 */
export function formatSessionCost(cost: { computeBlocks: number | null; estimated: boolean } | undefined): string {
  if (!cost || cost.computeBlocks == null || !Number.isFinite(cost.computeBlocks)) return '—';
  return formatComputeBlocks(cost.computeBlocks, cost.estimated);
}

/** The stats block's turn count — "23 turns", "1 turn". */
export function formatSessionTurns(turns: number): string {
  return `${turns} ${turns === 1 ? 'turn' : 'turns'}`;
}

/* ------------------------------- uploads -------------------------------- */

/**
 * The session's CURRENT uploads (FRONT-358) — every file staged via a
 * user_message, minus those later removed, replayed in tape order so a
 * re-upload after a removal correctly reappears. Same-named uploads
 * overwrite in the repo, so the name is the identity here too: a later
 * upload of "logo.png" replaces the earlier chip instead of duplicating it.
 */
/* ------------------------ live run activity (FRONT-1746) ----------------- */

/**
 * Present-tense counterpart to `summarizeToolUse`. That one narrates the
 * transcript after the fact ("Wrote index.html"); the strip narrates what is
 * happening right now ("Writing index.html"), so the tense has to differ even
 * though the inputs are identical. Kept as its own switch rather than a tense
 * flag on the original: the two read differently enough per tool (a past
 * "Searched for x" vs a live "Searching for x") that branching inside one
 * function would be harder to follow than two small ones.
 */
export function describeToolActivity(tool: string, inputJson: string): string {
  const name = shortToolName(tool);
  const input = parseToolInput(inputJson);
  const path = str(input.path);
  switch (name) {
    case 'write_file':
      return input.append ? `Appending to ${path || 'a file'}` : `Writing ${path || 'a file'}`;
    case 'read_file':
      return `Reading ${path || 'a file'}`;
    case 'view_image':
      return `Looking at ${path || 'an image'}`;
    case 'list_files':
      return path ? `Listing ${path}` : 'Listing project files';
    case 'search_files':
      return `Searching for ${truncate(str(input.query), 40) || 'files'}`;
    case 'run_command':
      return `Running: ${truncate(str(input.cmd), 50) || 'a command'}`;
    case 'open_preview':
      return 'Opening the draft in the browser';
    case 'open_page':
      return `Looking at ${truncate(str(input.url), 40) || 'a web page'}`;
    case 'screenshot':
      return 'Looking at the page';
    case 'click':
    case 'fill':
      return 'Trying the page';
    case 'read_page':
      return 'Reading the page';
    case 'page_errors':
      return 'Checking for errors';
    case 'close_page':
      return 'Closing the page';
    default:
      return `Using ${name}`;
  }
}

export interface RunActivity {
  /** What Eddy is doing right now, or null when nothing more specific than
   *  "working" is known (a run that has started but not yet called a tool). */
  label: string | null;
  /** ISO timestamp from the in-flight `run_started` event, for the elapsed
   *  counter. Null on a run started before FRONT-1746 added it — the strip
   *  then shows the activity without a timer rather than a wrong one. */
  startedAt: string | null;
}

/**
 * What the current run is doing, for the status strip (FRONT-1746 item 1).
 *
 * Only ever describes an IN-FLIGHT run: scanning backward, a terminal
 * boundary (finished/failed/interrupted) means the run is over and there is
 * no live activity to report, so this returns null rather than narrating a
 * stale tool call as if it were still happening. That guard is the whole
 * reason this scans instead of just grabbing the last `tool_use` on the tape.
 *
 * `run_resumed` counts as in-flight too (FRONT-987 auto-resume after a
 * restart) — the run genuinely is running again, and its own `run_started`
 * may be far back on the tape or absent entirely.
 */
export function deriveRunActivity(events: CodingEvent[]): RunActivity | null {
  let label: string | null = null;
  // Tool calls whose result has already landed. Scanning backward we meet a
  // result BEFORE its own call, so by the time we reach a tool_use we already
  // know whether it finished.
  const resolved = new Set<string>();
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    switch (e.name) {
      case 'run_finished':
      case 'run_failed':
      case 'run_interrupted':
        return null;
      case 'run_started':
      case 'run_resumed': {
        const startedAt = str(e.payload?.startedAt);
        return { label, startedAt: startedAt || null };
      }
      case 'tool_result': {
        const id = str(e.payload?.toolUseId);
        if (id) resolved.add(id);
        break;
      }
      case 'tool_use': {
        // FRONT-1746 fix: only an UNRESOLVED call is happening now. Before
        // this, the newest tool_use won unconditionally, so the strip kept
        // narrating a call that had already finished — or already FAILED,
        // which is worse: a failed read showed as "Reading index.html" for
        // as long as the run continued, while the transcript beside it
        // showed the same call with an error mark. Between tools the label
        // stays null and the strip shows the status alone, which is honest:
        // the model is thinking or writing prose, and the tape cannot say
        // which until the next call or text lands.
        // BLOCKING_QUESTION_TOOLS is excluded for the same reason
        // buildTranscript hides its activity row: each blocks for up to the
        // full ask timeout, so it would sit in the strip for minutes. Worse
        // here than there — it would read "waiting for you · Using
        // confirm_build", pairing the raw MCP tool name with the very status
        // that already says the user is the blocker.
        if (
          label === null &&
          !BLOCKING_QUESTION_TOOLS.has(str(e.payload?.tool)) &&
          !resolved.has(str(e.payload?.toolUseId))
        ) {
          label = describeToolActivity(str(e.payload?.tool), str(e.payload?.input));
        }
        break;
      }
    }
  }
  return null;
}

/* ------------------------ waiting reason (FRONT-1746) -------------------- */

/**
 * Why a `waiting` status is waiting (FRONT-1746 item 2) — so the strip can say
 * "waiting for your answer" instead of a bare "waiting".
 *
 * Only three reasons are actually representable from the tape:
 *   'answer'  — an open `question_asked` batch; the run is parked on the user.
 *   'stopped' — the last boundary was `run_interrupted`; the user stopped it.
 *   'paused'  — the last run reached its step limit (FRONT-2972).
 * Anything else (including an idle session that has simply never run) returns
 * null and the strip falls back to the plain status word.
 *
 * NOT covered: "waiting for approval". The ticket lists it, but no approval
 * event exists on the coding tape — the coding session's own consent-gated
 * publish goes through `ask_user`, which lands as `question_asked` and is
 * therefore already the 'answer' case. If a distinct approval state is ever
 * added server-side it needs its own event before it can be shown here;
 * inventing it from the current tape would be a guess.
 */
export type WaitingReason = 'answer' | 'stopped' | 'paused';

export function deriveWaitingReason(events: CodingEvent[]): WaitingReason | null {
  const scan = findOpenBatchOrBoundary(events);
  if (scan.kind === 'boundary' && scan.name !== 'run_started' && newestRunIsBackgroundCheck(events)) return null;
  if (scan.kind === 'open_batch') return 'answer';
  if (scan.kind === 'boundary' && scan.name === 'run_interrupted') return 'stopped';
  if (scan.kind === 'boundary' && scan.name === 'run_paused') return 'paused';
  return null;
}

export function deriveUploads(events: CodingEvent[]): UploadAttachment[] {
  const byName = new Map<string, UploadAttachment>();
  for (const event of events) {
    const p = event.payload ?? {};
    if (event.name === 'user_message') {
      for (const att of readUploadAttachments(p)) {
        byName.delete(att.name); // re-insert at the end (newest version wins)
        byName.set(att.name, att);
      }
    } else if (event.name === 'upload_removed') {
      const name = str(p.name);
      if (name) byName.delete(name);
    }
  }
  return [...byName.values()];
}

/**
 * The header strip's words for the four states.
 *
 * These are sentences about the BUILD, not bare adjectives about the session:
 * the design's "Build complete" is the anchor and the rest are worded to sit
 * beside it. (An earlier `STATUS_LABEL` map held the adjectives — "Working",
 * "Done" — for the bordered mono pill this strip replaced. Its last consumer
 * went with that pill, so it went too rather than lingering as a second
 * vocabulary nothing rendered.)
 */
export const BUILD_STATUS_LABEL: Record<CodingStatus, string> = {
  working: 'Building',
  checking: 'Checking in the background',
  waiting: 'Waiting for you',
  done: 'Build complete',
  failed: 'Build failed',
};

/**
 * The strip's state word. `waiting` is the one state that splits: the user
 * being the blocker ("Waiting for you") and a deliberate stop ("Build
 * stopped") are different things to a reader, and only the tape knows which.
 */
export function buildStatusLabel(status: CodingStatus, reason?: WaitingReason | null): string {
  if (status === 'waiting' && reason === 'stopped') return 'Build stopped';
  if (status === 'waiting' && reason === 'paused') return 'Build paused';
  return BUILD_STATUS_LABEL[status];
}

/* ---------------------------- project switcher --------------------------- */

export interface SessionOption {
  id: string;
  title: string;
}

/**
 * Options for the coding window's project switcher (FRONT-341). The attached
 * session is GUARANTEED to be among the returned options: a native <select>
 * whose value is missing from its rendered options silently displays the
 * first option without firing onChange — the dropdown then claims a
 * different project than the transcript on screen, which is exactly the
 * wrong-session-send trap. The attached id can legitimately be absent from
 * the list (stale shared cache, reorder after a background finish, the
 * 50-row list cap, or a Files/Apps `initialSessionId` outside the cached
 * page), so when it's missing we prepend it using the attached session
 * row's own title.
 */
export function buildSessionOptions(
  sessions: CodingSessionView[] | undefined,
  attachedId: string | null,
  attachedTitle: string | null,
): SessionOption[] {
  const base = (sessions ?? []).map((s) => ({ id: s.id, title: s.title }));
  if (!attachedId || base.some((o) => o.id === attachedId)) return base;
  return [{ id: attachedId, title: attachedTitle || '…' }, ...base];
}

/*
 * The composer-badge chain used to end this file (FRONT-339): `CodingActivity`,
 * `deriveCodingActivity`, and the `eddy.coding.lastSeenAt` stamp
 * (`getCodingLastSeenMs` / `markCodingSeen`) that told it whether a finished
 * run was still news. All of it existed for one thing — the coloured dot on
 * the chat composer's coding icon — and went with that icon on 2026-09-01,
 * when the top bar's Build button replaced the doorway. The signal was
 * deliberately not re-homed onto the new button: the status cluster ten pixels
 * away already folds Building Sessions into its counts and rows (FRONT-1681)
 * and raises the ask chip for one, so a second dot would say the same thing
 * twice. The stamp went too rather than being left as a write nobody reads.
 */

/**
 * FRONT-1787: the project-context textarea's `maxLength` — the same 4000 as
 * `CODE_CONTEXT_MAX_CHARS` in `src/coding/code-context-file.ts`, so anything
 * a USER types reaches the prompt in full.
 *
 * Deliberately a duplicated NUMBER rather than a shared module: the frontend
 * cannot import from `src/`, and drift here is benign in both directions. Too
 * low and the box stops a little early; too high and the file simply holds
 * more than one turn injects, which is already the designed behaviour for the
 * agent's own later additions (FRONT-1831) — the file is never truncated, only
 * the read is. Nothing silently breaks either way, which is why this is a
 * constant and not a wire value.
 */
export const CODING_CONTEXT_MAX_CHARS = 4000;
